import React, { useState } from "react";
import { CalendarClock, CalendarPlus, Coffee, Pencil, Settings2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WeeklyGrid } from "./WeeklyGrid";
import { RangeEditor } from "./RangeEditor";
import { OverridesCalendar } from "./OverridesCalendar";
import { DAYS, fmt12, isoDate, istToday } from "./scheduleUtils";

const DAY_FULL = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
export const NOTICE = [[60, "1 hour"], [120, "2 hours"], [240, "4 hours"], [720, "12 hours"], [1440, "24 hours"]];
export const HORIZON = [[7, "7 days"], [14, "14 days"], [30, "30 days"]];
const range = ([a, b]) => `${fmt12(a).toUpperCase()} – ${fmt12(b).toUpperCase()}`;
const label = (opts, v) => opts.find(([x]) => x === v)?.[1] || `${v}`;

function Card({ icon: Icon, eyebrow, title, children, testId, action }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1.5"><Icon size={12} /> {eyebrow}</p>
          <p className="text-sm text-slate-500 mt-0.5">{title}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Weekly summary derived client-side from the already-loaded `weekly` map. */
export function WeeklySummary({ weekly, onEdit }) {
  return (
    <Card icon={CalendarClock} eyebrow="Weekly hours" title="Your regular availability (IST)" testId="weekly-section"
          action={<Button onClick={onEdit} data-testid="edit-weekly" className="bg-[#D4AF37] text-[#0A2540] hover:bg-[#c9a430] font-bold"><Pencil size={14} className="mr-1" /> Edit weekly hours</Button>}>
      <ul className="mt-4 divide-y divide-slate-100" data-testid="weekly-summary">
        {DAYS.map((d) => (
          <li key={d} className="py-2.5 flex flex-wrap items-baseline gap-x-6 gap-y-1" data-testid={`weekly-${d}`}>
            <span className="w-28 font-extrabold text-[#0A2540]">{DAY_FULL[d]}</span>
            {weekly[d]?.length
              ? <span className="flex flex-wrap gap-x-6 gap-y-1 text-sm font-semibold text-slate-700">{weekly[d].map((w) => <span key={w.join()}>{range(w)}</span>)}</span>
              : <span className="text-sm font-semibold text-slate-400">Off</span>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Focused editor: time-range form (default on phones) or the existing 30-min grid (default on desktop). */
export function WeeklyEditorDialog({ open, weekly, onCancel, onSave, saving }) {
  const [draft, setDraft] = useState(weekly);
  const [mode, setMode] = useState("ranges");
  const Toggle = ({ v, label }) => (
    <button type="button" onClick={() => setMode(v)} aria-pressed={mode === v} data-testid={`editor-mode-${v}`}
            className={`h-8 px-3 rounded-full text-xs font-bold ${mode === v ? "bg-[#0A2540] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{label}</button>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className={`w-[calc(100vw-1rem)] ${mode === "grid" ? "max-w-4xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 rounded-2xl p-5 sm:p-6`} data-testid="weekly-editor">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Edit weekly hours</DialogTitle>
          <DialogDescription className="text-xs">
            {mode === "grid" ? "Tap or drag over 30-minute slots to make them available." : "Set the hours you're available each day, in 30-minute steps."} Repeats every week (IST).
          </DialogDescription>
        </DialogHeader>
        <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 p-0.5 self-start" data-testid="editor-mode-toggle"><Toggle v="ranges" label="Time ranges" /><Toggle v="grid" label="Grid" /></div>
        {mode === "grid"
          ? <div className="overflow-x-auto -mx-1 px-1"><div className="min-w-[640px]"><WeeklyGrid weekly={draft} onChange={setDraft} /></div></div>
          : <RangeEditor weekly={draft} onChange={setDraft} />}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="weekly-editor-cancel">Cancel</Button>
          <Button onClick={() => onSave(draft)} disabled={saving} data-testid="weekly-editor-save" className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Compact list of upcoming overrides from the already-loaded `overrides`; calendar lives in the Add dialog. */
export function SpecialDates({ overrides, weekly, onPick, onRemove }) {
  const [adding, setAdding] = useState(false);
  const today = isoDate(istToday());
  const upcoming = overrides.filter((o) => o.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return (
    <Card icon={CalendarPlus} eyebrow="Special dates" title="Change your hours or take a day off for a specific date." testId="overrides-section"
          action={<Button variant="outline" onClick={() => setAdding(true)} data-testid="add-special-date" className="font-bold">+ Add special date</Button>}>
      <p className="mt-4 text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Upcoming overrides</p>
      {upcoming.length === 0 ? (
        <p className="mt-2 text-sm text-slate-400" data-testid="no-overrides">None — your weekly hours apply to every date.</p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100" data-testid="override-list">
          {upcoming.map((o) => (
            <li key={o.date} className="py-2.5 flex flex-wrap items-start gap-x-5 gap-y-1" data-testid={`override-${o.date}`}>
              <span className="w-16 font-extrabold text-[#0A2540]">{fmtDate(o.date)}</span>
              <div className="flex-1 min-w-[140px]">
                <p className={`text-sm font-bold ${o.windows?.length ? "text-slate-700" : "text-amber-700"}`}>{o.windows?.length ? "Custom hours" : "Day off"}</p>
                {!!o.windows?.length && <p className="text-xs text-slate-500">{o.windows.map(range).join(" · ")}</p>}
                {o.reason && <p className="text-xs text-slate-400 italic">{o.reason}</p>}
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => onPick(o.date)} data-testid={`override-edit-${o.date}`} className="h-8 px-2.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100">Edit</button>
                <button type="button" onClick={() => onRemove(o.date)} data-testid={`override-remove-${o.date}`} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50" aria-label="Remove override"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 rounded-2xl p-5 sm:p-6" data-testid="special-date-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Add special date</DialogTitle>
            <DialogDescription className="text-xs">Tap a date to set different hours or a day off. Your weekly schedule is not changed.</DialogDescription>
          </DialogHeader>
          <OverridesCalendar overrides={overrides} weekly={weekly} onPick={(d) => { setAdding(false); onPick(d); }} onRemove={onRemove} />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/** Minimum notice + horizon as a compact read view; "Edit settings" reveals the existing selects. */
export function BookingSettings({ notice, horizon, onNotice, onHorizon }) {
  const [editing, setEditing] = useState(false);
  return (
    <Card icon={Settings2} eyebrow="Booking settings" title="Rules for scheduled bookings." testId="booking-settings"
          action={<Button variant="outline" onClick={() => setEditing((v) => !v)} data-testid="edit-settings" className="font-bold">{editing ? "Done" : "Edit settings"}</Button>}>
      <dl className="mt-4 space-y-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <dt className="text-slate-600">Minimum notice</dt>
          <dd className="font-extrabold text-[#0A2540]" data-testid="notice-value">
            {editing ? <select value={notice} onChange={(e) => onNotice(Number(e.target.value))} data-testid="min-notice" className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold bg-white">{NOTICE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select> : label(NOTICE, notice)}
          </dd>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <dt className="text-slate-600">Clients can book up to</dt>
          <dd className="font-extrabold text-[#0A2540]" data-testid="horizon-value">
            {editing ? <select value={horizon} onChange={(e) => onHorizon(Number(e.target.value))} data-testid="horizon" className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold bg-white">{HORIZON.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select> : `${label(HORIZON, horizon)} ahead`}
          </dd>
        </div>
      </dl>
      {editing && <p className="mt-3 text-[11px] text-slate-400">Minimum notice does not affect instant consultations while you are online.</p>}
    </Card>
  );
}

export function VacationCard({ on, upcomingCount, onToggle }) {
  return (
    <Card icon={Coffee} eyebrow="Vacation mode" title={on ? "You won't receive new bookings while vacation mode is active." : "Your regular schedule remains active."} testId="vacation-section"
          action={<span className={`text-[11px] font-extrabold rounded-full border px-2.5 py-1 ${on ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-500"}`} data-testid="vacation-state">{on ? "On" : "Currently off"}</span>}>
      {on && upcomingCount > 0 && (
        <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="vacation-upcoming">You still have {upcomingCount} upcoming paid consultation{upcomingCount > 1 ? "s" : ""} — they remain booked.</p>
      )}
      <Button onClick={onToggle} variant="outline" data-testid="vacation-toggle" className={`mt-4 w-full font-bold ${on ? "border-amber-300 text-amber-800" : ""}`}>
        {on ? "End vacation" : "Turn on vacation"}
      </Button>
    </Card>
  );
}
